import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Typography from '@primitives/Typography';
import CloseIcon from '@icons/CloseIcon';
import { DomElementIds } from '@constants';
import { DrawerProps } from './types';
import ReactPortal from '../ReactPortal';
import styles from './Drawer.module.scss';
import { useKeyListener } from '@hooks';
import { ESCAPE_KEY } from '@constants/Components';

const Drawer: React.FC<DrawerProps> = ({
    open = false,
    id,
    onClose,
    children,
    usePadding = true,
    headerText = '',
    showHeaderDivider = false,
    footer = false,
    className,
    headerContainerClasses,
    footerContainerClasses,
    disableOutsideClick,
    onOutSideClick,
    customHeader,
    customHeaderClass,
    contentClasses,
    customIcon,
    ...restProps
}) => {
    const drawerPortalId = id || DomElementIds.drawerPortalId;
    const [headerClassList, setHeaderClassList] = useState<string>(styles['drawer__header']);
    const closeIconRef = useRef<HTMLSpanElement>(null);
    const drawerRef = useRef<HTMLDivElement>(null);

    const drawerContainerStyles = cx(styles['drawer-container'], {
        [styles['is-open']]: open,
    });

    const drawerContentStyles = cx(styles['drawer__content'], {
        [styles['drawer__content--with-padding']]: usePadding,
        [styles['drawer__content--with-header']]: !showHeaderDivider,
    });

    useEffect(() => {
        setHeaderClassList(
            cx(
                styles['drawer__header'],
                {
                    [styles['drawer__header--with-divider']]: showHeaderDivider,
                },
                headerContainerClasses,
            ),
        );
    }, [showHeaderDivider]);

    const handlClose = (event?: MouseEvent): void => {
        document.body.style.overflow = 'unset';
        onClose(event);
        if (event) {
            event.stopPropagation();
        }
    };

    const handleClick = (e: MouseEvent): void => {
        const isCloseIconClicked = closeIconRef.current?.contains(e.target as Node);
        const isDrawerClicked = drawerRef.current?.contains(e.target as Node);
        if (isDrawerClicked) {
            if (isCloseIconClicked) handlClose(e);
        } else {
            if (!disableOutsideClick) handlClose(e);
            onOutSideClick?.(e);
        }
    };

    if (open) document.body.style.overflow = 'hidden';

    const handleDrawerScroll = (event: any): void => {
        !showHeaderDivider &&
            setHeaderClassList(
                cx(
                    styles['drawer__header'],
                    {
                        [styles['drawer__header--with-shadow']]: event.target.scrollTop !== 0,
                    },
                    headerContainerClasses,
                ),
            );
    };

    const renderHeaderText = (): ReactNode => {
        if (typeof headerText === 'string') {
            return headerText.length > 0 && <Typography variant="h3">{headerText}</Typography>;
        }
        return headerText;
    };

    const renderFooter = (): JSX.Element | false => {
        return (
            footer && (
                <div className={cx(styles['drawer__footer'], footerContainerClasses)}>{footer}</div>
            )
        );
    };
    const renderHeader = (): ReactNode => {
        if (customHeader) {
            return <div className={customHeaderClass}>{customHeader}</div>;
        } else {
            return renderHeaderText();
        }
    };

    const renderHeaderIcon = (): ReactNode => {
        if (customIcon) {
            return customIcon;
        } else {
            return <CloseIcon size="sm" className={styles['close-icon']} />;
        }
    };

    useKeyListener(ESCAPE_KEY, handlClose, open);

    return (
        <ReactPortal wrapperId={drawerPortalId}>
            <div
                className={drawerContainerStyles}
                data-testid="drawer-overlay"
                onClick={handleClick}
            >
                <div ref={drawerRef} className={cx(styles['drawer'], className)} {...restProps}>
                    <div className={headerClassList}>
                        <span ref={closeIconRef} data-testid="drawer-close-icon">
                            {renderHeaderIcon()}
                        </span>
                        {renderHeader()}
                    </div>
                    <div
                        className={cx(drawerContentStyles, contentClasses)}
                        data-testid="drawer-content"
                        onScroll={handleDrawerScroll}
                    >
                        {children}
                    </div>
                    {renderFooter()}
                </div>
            </div>
        </ReactPortal>
    );
};

export default Drawer;
